import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DrilldownContainerChartSize, KpiTimeCycleChartData } from '@models';
import { EChartsOption } from 'echarts';
import { ECharts } from 'echarts/core';

type PortfolioKpiChartCardVariant = 'side' | 'expanded';
type PortfolioKpiChartCardType = 'cycle' | 'summary';
interface MilestoneStatusItem {
  label: string;
  value: number;
}
const DEFAULT_MILESTONE_STATUS_SUMMARY_DATA: MilestoneStatusItem[] = [
  { label: 'Upcoming', value: 12 },
  { label: 'On Track', value: 10 },
  { label: 'At Risk', value: 9 },
  { label: 'Past Due', value: 8 },
  { label: 'Completed', value: 15 },
  { label: 'Completed | Accelerated', value: 8 },
  { label: 'Completed | On Time', value: 6 },
  { label: 'Completed | Late', value: 8 }
];
const DEFAULT_TIME_CYCLE_CHART_DATA: KpiTimeCycleChartData = {
  identifier: 'timeCycleChart',
  title: 'Average cycle time',
  tooltipText:
    'Cycle time is the elapsed time from the first milestone’s start to the second milestone’s completion.',
  labels: [],
  offset: [],
  timeWeeks: [],
  category: [],
  cycleCount: 0
};

@Component({
  selector: 'kpi-chart-card',
  templateUrl: './kpi-chart-card.component.html',
  styleUrl: './kpi-chart-card.component.scss'
})
export class KpiChartCardComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('summaryChartContainer') summaryChartContainer?: ElementRef<HTMLElement>;
  @Input() title = '';
  @Input() variant: PortfolioKpiChartCardVariant | 'fullscreen' = 'side';
  @Input() cardType: PortfolioKpiChartCardType = 'cycle';
  @Input() isLoading = false;
  @Input() showHeader = true;
  @Input() showCycleTooltipText = true;
  @Input() cycleInfoTitle = 'Cycle time';
  @Input() cycleInfoTooltipText = '';
  @Input() framed = true;
  @Input() infoTooltipTitle = '';
  @Input() infoTooltipText = '';
  @Input() milestoneStatusSummaryData: MilestoneStatusItem[] =
    DEFAULT_MILESTONE_STATUS_SUMMARY_DATA;
  @Input() timeCycleChartData: KpiTimeCycleChartData = DEFAULT_TIME_CYCLE_CHART_DATA;
  @Input() xAxisLabel = 'Number of milestones';

  @Output() expandClicked = new EventEmitter<void>();
  @Output() summaryStatusClicked = new EventEmitter<string>();
  private chartInstance?: ECharts;
  private resizeObserver?: ResizeObserver;
  private pendingChartRefresh = false;
  cycleInfoTooltipOpen = false;
  infoTooltipOpen = false;

  onExpand(): void {
    this.expandClicked.emit();
  }

  onChartInit(chart: ECharts): void {
    this.chartInstance = chart;
    this.applyChartUpdate();
  }

  onSummaryChartClick(event: any): void {
    if (this.cardType !== 'summary' || this.isLoading) {
      return;
    }

    const label = this.getSummaryLabelFromChartEvent(event);
    if (!label) {
      return;
    }

    this.summaryStatusClicked.emit(this.mapSummaryLabelToStudyMilestoneStatus(label));
  }

  onCycleInfoEnter(): void {
    this.cycleInfoTooltipOpen = true;
  }

  onCycleInfoLeave(): void {
    this.cycleInfoTooltipOpen = false;
  }

  onCycleInfoToggle(): void {
    this.cycleInfoTooltipOpen = !this.cycleInfoTooltipOpen;
  }

  onInfoEnter(): void {
    this.infoTooltipOpen = true;
  }

  onInfoLeave(): void {
    this.infoTooltipOpen = false;
  }

  onInfoToggle(): void {
    this.infoTooltipOpen = !this.infoTooltipOpen;
  }

  get cycleInfoText(): string {
    if (this.cycleInfoTooltipText.trim()) {
      return this.cycleInfoTooltipText.trim();
    }

    return this.timeCycleChartData?.tooltipText?.trim() || 'No information available';
  }

  get hasInfoTooltip(): boolean {
    return this.cardType !== 'cycle' && this.infoTooltipText.trim().length > 0;
  }

  get cycleChartSize(): DrilldownContainerChartSize {
    return this.variant === 'side' ? 'small' : 'medium';
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    this.queueChartResize();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['milestoneStatusSummaryData'] ||
      changes['variant'] ||
      changes['cardType'] ||
      changes['xAxisLabel']
    ) {
      this.applyChartUpdate();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.queueChartResize();
  }

  get summaryChartOption(): EChartsOption {
    const data = this.getNormalizedSummaryData();
    const labels = data.map((item) => item.label);
    const values = data.map((item) => item.value);
    const barWidth = this.getDynamicBarWidth(data.length);
    const maxValue = Math.max(...values, 0);
    const axisInterval = this.getAxisInterval(maxValue);
    const axisMax = axisInterval * 4;
    const isSideSummary = this.variant === 'side';
    const isExpandedSummary = this.variant === 'expanded';
    const isFullscreenSummary = this.variant === 'fullscreen';
    const showXAxisTitle = !isSideSummary;
    const grid = isSideSummary
      ? {
          top: 0,
          right: 12,
          bottom: 26,
          left: 96,
          containLabel: false
        }
      : isExpandedSummary
        ? {
            top: 8,
            right: 0,
            bottom: 42,
            left: 0,
            containLabel: true
          }
        : isFullscreenSummary
          ? {
              top: 8,
              right: 12,
              bottom: 42,
              left: 20,
              containLabel: true
            }
          : {
              top: 8,
              right: 16,
              bottom: 42,
              left: 0,
              containLabel: true
            };

    return {
      grid,
      xAxis: {
        type: 'value',
        min: 0,
        max: axisMax,
        interval: axisInterval,
        splitNumber: 4,
        name: showXAxisTitle ? this.xAxisLabel : '',
        nameLocation: 'middle',
        nameGap: showXAxisTitle ? 34 : 0,
        splitLine: {
          show: true,
          lineStyle: { color: '#cccccc', width: 1 }
        },
        axisLine: { show: true, lineStyle: { color: '#92929e', width: 1 } },
        axisTick: { show: false },
        axisLabel: {
          color: '#1b1b1d',
          fontSize: 12,
          formatter: (value: number) => `${Math.round(value)}`,
          fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
        },
        nameTextStyle: {
          color: '#1b1b1d',
          fontSize: 14,
          fontWeight: 700,
          fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
        }
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: labels,
        axisTick: { show: false },
        axisLine: { show: true, lineStyle: { color: '#92929e', width: 1 } },
        axisLabel: {
          color: '#1b1b1d',
          fontSize: 12,
          margin: 10,
          formatter: (value: string) =>
            isSideSummary ? value.replace(/\s*\|\s*/g, '\n| ') : value,
          fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
        }
      },
      series: [
        {
          type: 'bar',
          data: values,
          barWidth,
          barMinHeight: 0,
          cursor: 'pointer',
          z: 3,
          itemStyle: { color: '#2aa4bf', opacity: 1 }
        }
      ]
    };
  }

  private getAxisInterval(maxValue: number): number {
    if (maxValue <= 0) return 1;
    return Math.max(1, Math.ceil(maxValue / 4));
  }

  private getDynamicBarWidth(categoryCount: number): number | string {
    if (!categoryCount || categoryCount < 1) return 12;

    if (categoryCount === 1) {
      return this.variant === 'side' ? '50%' : '75%';
    }

    const availableHeight =
      this.summaryChartContainer?.nativeElement?.clientHeight || this.getEstimatedChartHeight();
    const perCategorySlot = availableHeight / categoryCount;
    const width = Math.floor(perCategorySlot * 0.62);

    return Math.max(10, Math.min(45, width));
  }

  private getEstimatedChartHeight(): number {
    if (this.variant === 'side') return 439;
    if (this.variant === 'expanded') return 265;
    return 520;
  }

  private setupResizeObserver(): void {
    if (!this.summaryChartContainer?.nativeElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.queueChartResize();
    });
    this.resizeObserver.observe(this.summaryChartContainer.nativeElement);
  }

  private applyChartUpdate(): void {
    if (this.cardType !== 'summary') {
      return;
    }

    if (!this.chartInstance) {
      this.pendingChartRefresh = true;
      return;
    }

    this.chartInstance.setOption(this.summaryChartOption, { notMerge: true, lazyUpdate: false });
    this.pendingChartRefresh = false;
    this.queueChartResize();
  }

  private queueChartResize(): void {
    if (!this.chartInstance) return;

    requestAnimationFrame(() => {
      this.chartInstance?.resize();
      setTimeout(() => this.chartInstance?.resize(), 80);
      setTimeout(() => this.chartInstance?.resize(), 220);
      if (this.pendingChartRefresh) {
        this.chartInstance?.setOption(this.summaryChartOption, {
          notMerge: true,
          lazyUpdate: false
        });
        this.pendingChartRefresh = false;
        this.chartInstance?.resize();
      }
    });
  }

  private getNormalizedSummaryData(): MilestoneStatusItem[] {
    const source = this.milestoneStatusSummaryData?.length
      ? this.milestoneStatusSummaryData
      : DEFAULT_MILESTONE_STATUS_SUMMARY_DATA;

    return source.map((item, idx) => {
      const fallback = DEFAULT_MILESTONE_STATUS_SUMMARY_DATA[idx] ?? {
        label: item?.label ?? '',
        value: 0
      };
      const parsedValue = Number((item as any)?.value);
      return {
        label: item?.label || fallback.label,
        value: Number.isFinite(parsedValue) ? parsedValue : fallback.value
      };
    });
  }

  private mapSummaryLabelToStudyMilestoneStatus(label: string): string {
    const normalizedLabel = label.trim().toLowerCase();
    if (normalizedLabel === 'on track') return 'On Track';
    if (normalizedLabel === 'at risk') return 'At Risk';
    if (normalizedLabel === 'past due') return 'Past Due';
    if (normalizedLabel === 'upcoming') return 'Upcoming';
    return label;
  }

  private getSummaryLabelFromChartEvent(event: any): string {
    const name = event?.name?.toString?.().trim?.();
    if (name) return name;

    const dataIndex = Number(event?.dataIndex);
    if (Number.isInteger(dataIndex) && dataIndex >= 0) {
      const item = this.getNormalizedSummaryData()[dataIndex];
      return item?.label ?? '';
    }

    return '';
  }
}
